'use client';

import { useEffect, useRef, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { userApi } from '@/api/features/users';
import { organizationsApi } from '@/api/features/organizations';
import { organizationsMembersApi } from '@/api/features/organizations/endpoints/members';
import { foldersApi } from '@/api/features/folders/endpoints/core';
import { teamChatApi } from '@/api/features/teams';
import { conversationQueriesApi } from '@/api/features/conversations';
import { reduxLogger } from '@/core/utils/logger';

interface PrefetchConfig {
  enabled: boolean;
  priority: 'critical' | 'high' | 'low';
  section?: string;
}

/**
 * Central prefetch coordinator - prevents duplicate prefetching
 * and manages timing to avoid conflicts
 */
export function usePrefetchCoordinator(config: PrefetchConfig) {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(state => !!state.userProfile.session);
  const organizationId = useAppSelector(state => state.userProfile.session?.organizationId);
  const user = useAppSelector(state => state.userProfile.session);
  
  // Track what's been prefetched this session
  const prefetchedRef = useRef<Set<string>>(new Set());
  const prefetchTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);
  
  const prefetchData = useCallback(async (section?: string) => {
    if (!isAuthenticated || !config.enabled) return;
    
    // Build prefetch key
    const prefetchKey = `${config.priority}-${section || 'all'}-${Date.now()}`;
    
    // Check if we've already prefetched recently (within 5 minutes)
    const recentKeys = Array.from(prefetchedRef.current).filter(key => {
      const [, , timestamp] = key.split('-');
      return Date.now() - parseInt(timestamp) < 5 * 60 * 1000;
    });
    
    if (recentKeys.some(key => key.startsWith(`${config.priority}-${section || 'all'}`))) {
      reduxLogger.debug('[PrefetchCoordinator] Skipping duplicate prefetch:', prefetchKey);
      return;
    }
    
    prefetchedRef.current.add(prefetchKey);
    
    try {
      const promises: unknown[] = [];

      // Critical data - always prefetch
      if (config.priority === 'critical') {
        promises.push(
          dispatch(userApi.util.prefetch('getUserProfile', undefined, { force: false }))
        );

        if (organizationId) {
          promises.push(
            dispatch(organizationsApi.util.prefetch('getOrganizationDetails', organizationId, { force: false }))
          );
        }
      }

      // High priority - section specific
      if (config.priority === 'high' && section) {
        switch (section) {
          case 'settings':
            if (organizationId) {
              promises.push(
                dispatch(organizationsApi.util.prefetch('getOrganizationPhoneNumbers', organizationId, { force: false })),
                dispatch(organizationsMembersApi.util.prefetch('getOrganizationMembers', undefined, { force: false }))
              );
            }
            promises.push(
              dispatch(foldersApi.util.prefetch('getOrganizationFolders', undefined, { force: false }))
            );
            break;
            
          case 'teams':
            if (user?.id) {
              promises.push(
                dispatch(teamChatApi.util.prefetch('getTeamConversations', { currentUserId: user.id } as Record<string, unknown>, {
                  force: false
                })),
                dispatch(organizationsMembersApi.util.prefetch('getOrganizationMembers', undefined, { 
                  force: false 
                }))
              );
            }
            break;
            
          case 'conversations':
            promises.push(
              dispatch(conversationQueriesApi.util.prefetch('getConversations', {
                search: '',
                tagIds: [],
                view: 'all',
                limit: 20,
                page: 0,
                includeArchived: false,
                light: true,
              }, { force: false }))
            );
            break;
        }
      }
      
      // Low priority - background warmup
      if (config.priority === 'low' && !section) {
        // Only prefetch if idle
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(() => {
            if (user?.id) {
              dispatch(teamChatApi.util.prefetch('getTeamConversations', { currentUserId: user.id } as Record<string, unknown>, {
                force: false
              }));
            }
          }, { timeout: 2000 });
        }
      }
      
      if (promises.length > 0) {
        reduxLogger.info(`[PrefetchCoordinator] Prefetching ${promises.length} queries (${config.priority} priority)`);
        await Promise.allSettled(promises);
      }
    } catch (error) {
      reduxLogger.debug('[PrefetchCoordinator] Prefetch error (non-critical):', error);
    }
  }, [dispatch, isAuthenticated, organizationId, user, config]);
  
  useEffect(() => {
    if (!config.enabled || !isAuthenticated) return;
    
    // Clear any existing timer
    if (prefetchTimerRef.current) {
      clearTimeout(prefetchTimerRef.current);
    }
    
    // Delay based on priority
    const delay = config.priority === 'critical' ? 0 : 
                  config.priority === 'high' ? 100 : 
                  1000;
    
    prefetchTimerRef.current = setTimeout(() => {
      prefetchData(config.section);
    }, delay);
    
    return () => {
      if (prefetchTimerRef.current) {
        clearTimeout(prefetchTimerRef.current);
      }
    };
  }, [config.enabled, config.priority, config.section, isAuthenticated, prefetchData]);
  
  // Clean up old entries periodically
  useEffect(() => {
    const cleanup = setInterval(() => {
      const now = Date.now();
      prefetchedRef.current = new Set(
        Array.from(prefetchedRef.current).filter(key => {
          const [, , timestamp] = key.split('-');
          return now - parseInt(timestamp) < 10 * 60 * 1000; // Keep for 10 minutes
        })
      );
    }, 60000); // Clean every minute
    
    return () => clearInterval(cleanup);
  }, []);
  
  return { prefetchData };
}